/**
 * PluginBridge — Host-side bridge for communicating with plugin iframes.
 *
 * Handles postMessage communication, permission enforcement,
 * rate limiting, and proxying requests to the FIAS backend.
 */

/**
 * Plugin permission scopes (matches @fias/db-types PluginPermission).
 * Redefined here to avoid client dependency on server-only package.
 *
 * SYNC NOTE: Keep in sync with:
 *   - packages/db-types/src/plugin.ts (PluginPermission)
 *   - packages/arche-sdk/src/types.ts (PluginPermission)
 */
type PluginPermission =
  | 'user:profile:read'
  | 'entities:invoke'
  | 'entities:image_generate'
  | 'storage:sandbox'
  | 'theme:read'
  | 'data:store'
  | 'store:purchase'
  | 'files:read'
  | 'files:write'
  | 'assets:read';

// Bridge message types (must match the SDK's types)
interface BridgeMessage {
  type: string;
  messageId: string;
  payload: unknown;
}

interface PluginBridgeOptions {
  archId: string;
  permissions: PluginPermission[];
  iframe: HTMLIFrameElement;
  onResize?: (height: number) => void;
  onToast?: (message: string, variant: string) => void;
  onNavigate?: (path: string) => void;
  getTheme: () => unknown;
  getUser: () => Promise<{ userId: string; displayName: string; avatar: string | null }>;
  bridgeApiUrl: string;
  getAuthToken: () => Promise<string>;
  /** When true, uses local mocks for storage and custom handler for entity invoke. */
  previewMode?: boolean;
  /** Custom entity_invoke handler for preview (avoids backend proxy for entity lookup). */
  onEntityInvoke?: (payload: {
    entityId: string;
    input: string;
    systemPrompt?: string;
  }) => Promise<{ output: string }>;
  /** Custom image_generate handler for preview (avoids backend proxy for image config lookup). */
  onImageGenerate?: (payload: {
    entityId: string;
    prompt: string;
    size?: string;
    quality?: string;
    style?: string;
  }) => Promise<{
    imageUrl: string;
    imageRef: string;
    revisedPrompt?: string;
    provider: string;
    model: string;
  }>;
  /** External storage map for preview mode — survives bridge rebuilds when passed from parent. */
  previewStorage?: Map<string, string>;
  /** Custom storage handler for preview mode — when provided, proxies to backend instead of in-memory. */
  onStorage?: (action: string, params: Record<string, unknown>) => Promise<unknown>;
  /** Callback for bridge-level log events (e.g., burst detection warnings). */
  onLog?: (level: 'warn' | 'error', message: string) => void;
  /**
   * Callback for IAP purchase requests. When set, store_purchase messages
   * invoke this instead of proxying directly to backend. The callback should
   * show a confirmation UI and resolve with the backend response on confirm,
   * or { success: false, error: 'USER_CANCELED' } on cancel.
   */
  onPurchaseRequest?: (product: {
    productIdentifier: string;
    name: string;
    description: string | null;
    priceCredits: number;
    type: string;
  }) => Promise<unknown>;
  /** Mock store products for preview mode. */
  previewStoreProducts?: Array<{
    productIdentifier: string;
    type: string;
    name: string;
    description: string | null;
    priceCredits: number;
    metadata: Record<string, unknown>;
    subscriptionPeriod?: string;
  }>;
}

/**
 * Rate limiting state per message type.
 */
interface RateLimitBucket {
  count: number;
  windowStart: number;
}

const RATE_LIMITS: Record<string, { maxPerMinute: number }> = {
  entity_invoke: { maxPerMinute: 60 },
  image_generate: { maxPerMinute: 10 },
  storage_write: { maxPerMinute: 120 },
  storage_read: { maxPerMinute: 300 },
  storage_list: { maxPerMinute: 60 },
  storage_delete: { maxPerMinute: 60 },
  data_put: { maxPerMinute: 120 },
  data_get: { maxPerMinute: 300 },
  data_query: { maxPerMinute: 60 },
  data_delete: { maxPerMinute: 60 },
  data_create_collection: { maxPerMinute: 10 },
  data_delete_collection: { maxPerMinute: 10 },
  data_list_collections: { maxPerMinute: 60 },
  store_get_products: { maxPerMinute: 60 },
  store_purchase: { maxPerMinute: 10 },
  store_get_entitlements: { maxPerMinute: 60 },
  store_get_purchase_history: { maxPerMinute: 30 },
  store_restore: { maxPerMinute: 10 },
  store_cancel_subscription: { maxPerMinute: 10 },
  files_list: { maxPerMinute: 120 },
  files_read: { maxPerMinute: 300 },
  files_download_url: { maxPerMinute: 300 },
  files_write: { maxPerMinute: 60 },
  files_update: { maxPerMinute: 60 },
  files_delete: { maxPerMinute: 30 },
  arche_assets_list: { maxPerMinute: 60 },
  arche_assets_get_url: { maxPerMinute: 300 },
};

/** Burst detection: catches runaway loops (e.g., useEffect calling dataStore.put every render). */
interface BurstBucket {
  count: number;
  windowStart: number;
  /** When set, all calls of this type are blocked until this timestamp. */
  blockedUntil?: number;
}

const BURST_THRESHOLD = 50;
const BURST_WINDOW_MS = 5_000;
const BURST_COOLDOWN_MS = 10_000;

/**
 * Extracts a short diagnostic identifier from a bridge call's payload so that
 * rate-limit and runaway-loop errors can point to the specific path/resource
 * being hammered (e.g. `__state/gameState` when a game loop is spamming
 * `usePersistentState` writes). Returns an empty string for call types where
 * no sensible target exists.
 */
function describeBridgeTarget(type: string, payload: unknown): string {
  if (!payload || typeof payload !== 'object') return '';
  const p = payload as Record<string, unknown>;

  switch (type) {
    case 'storage_read':
    case 'storage_write':
    case 'storage_delete':
      return typeof p.path === 'string' ? `path: ${p.path}` : '';
    case 'storage_list':
      return typeof p.prefix === 'string' ? `prefix: ${p.prefix}` : '';
    case 'data_put':
    case 'data_get':
    case 'data_delete': {
      const collection = typeof p.collection === 'string' ? p.collection : '';
      const docId = typeof p.docId === 'string' ? p.docId : '';
      if (collection && docId) return `${collection}/${docId}`;
      return collection ? `collection: ${collection}` : '';
    }
    case 'data_query':
    case 'data_create_collection':
    case 'data_delete_collection':
      return typeof p.collection === 'string' ? `collection: ${p.collection}` : '';
    case 'entity_invoke':
      return typeof p.entityId === 'string' ? `entity: ${p.entityId}` : '';
    case 'navigate':
      return typeof p.path === 'string' ? `path: ${p.path}` : '';
    case 'files_read':
    case 'files_download_url':
    case 'files_update':
    case 'files_delete':
      return typeof p.fileId === 'string' ? `fileId: ${p.fileId}` : '';
    case 'files_write':
      return typeof p.name === 'string' ? `name: ${p.name}` : '';
    case 'files_list':
      return typeof p.search === 'string' ? `search: ${p.search}` : '';
    default:
      return '';
  }
}

// Thrown by checkBurst when a call is blocked for runaway-loop reasons. The
// burst path already fires onLog on the first breach and deliberately stays
// silent during cooldown — the outer request-failure catch uses this tag to
// skip its own onLog so we don't double-log the breach and re-log every
// cooldown-blocked call.
class BridgeBurstError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BridgeBurstError';
  }
}

// Preview-mode storage caps. Preview mode is explicitly not production —
// these caps exist so a preview-mode plugin can't pin the renderer tab by
// writing unbounded state into the in-memory Maps. Production writes go
// through the backend and are bounded by the datastore quota middleware.
const PREVIEW_STORAGE_MAX_ENTRIES = 10_000;
const PREVIEW_STORAGE_MAX_TOTAL_BYTES = 5 * 1024 * 1024; // 5 MB
const PREVIEW_DATASTORE_MAX_DOCS_PER_COLLECTION = 5_000;
const PREVIEW_DATASTORE_MAX_DOC_BYTES = 256 * 1024; // 256 KB per doc

/**
 * Permission requirements per bridge call type.
 */
const PERMISSION_MAP: Record<string, PluginPermission> = {
  get_user: 'user:profile:read',
  get_theme: 'theme:read',
  entity_invoke: 'entities:invoke',
  image_generate: 'entities:image_generate',
  storage_read: 'storage:sandbox',
  storage_write: 'storage:sandbox',
  storage_list: 'storage:sandbox',
  storage_delete: 'storage:sandbox',
  data_create_collection: 'data:store',
  data_list_collections: 'data:store',
  data_delete_collection: 'data:store',
  data_put: 'data:store',
  data_get: 'data:store',
  data_query: 'data:store',
  data_delete: 'data:store',
  store_get_products: 'store:purchase',
  store_purchase: 'store:purchase',
  store_get_entitlements: 'store:purchase',
  store_get_purchase_history: 'store:purchase',
  store_restore: 'store:purchase',
  store_cancel_subscription: 'store:purchase',
  files_list: 'files:read',
  files_read: 'files:read',
  files_download_url: 'files:read',
  files_write: 'files:write',
  files_update: 'files:write',
  files_delete: 'files:write',
  arche_assets_list: 'assets:read',
  arche_assets_get_url: 'assets:read',
};

export class PluginBridgeHost {
  private options: PluginBridgeOptions;
  private rateLimits = new Map<string, RateLimitBucket>();
  private burstBuckets = new Map<string, BurstBucket>();
  /** In-memory storage for preview mode (no backend calls needed). */
  private previewStorage: Map<string, string>;
  /** In-memory data store for preview mode — maps collection → (key → document). */
  private previewDataStore = new Map<string, Map<string, Record<string, unknown>>>();
  /** Collection metadata for preview data store. */
  private previewCollections = new Map<
    string,
    { name: string; userScope: string; createdAt: string }
  >();

  private initRetryTimers: ReturnType<typeof setTimeout>[] = [];

  constructor(options: PluginBridgeOptions) {
    this.options = options;
    this.previewStorage = options.previewStorage ?? new Map<string, string>();
    this.handleMessage = this.handleMessage.bind(this);
    window.addEventListener('message', this.handleMessage);
  }

  /**
   * Send the init message to the plugin iframe to establish the bridge.
   *
   * When loading via srcDoc, the iframe's onLoad fires before ES module
   * scripts (loaded from esm.sh) have executed. The SDK's message listener
   * may not exist yet, so we retry init a few times with increasing delays.
   * The SDK ignores duplicate init messages after the first.
   */
  init(): void {
    this.sendInit();
    this.initRetryTimers = [
      setTimeout(() => this.sendInit(), 200),
      setTimeout(() => this.sendInit(), 1000),
      setTimeout(() => this.sendInit(), 3000),
    ];
  }

  private sendInit(): void {
    this.sendToPlugin({
      type: 'init',
      messageId: 'init_0',
      payload: {
        archId: this.options.archId,
        permissions: this.options.permissions,
        theme: this.options.getTheme(),
        currentPath: window.location.pathname,
      },
    });
  }

  /**
   * Notify the plugin of a theme change.
   */
  sendThemeUpdate(): void {
    this.sendToPlugin({
      type: 'theme_update',
      messageId: `theme_${Date.now()}`,
      payload: this.options.getTheme(),
    });
  }

  /**
   * Navigate the plugin to a specific step/node.
   */
  navigateToStep(nodeId: string): void {
    this.sendToPlugin({
      type: 'step_navigate',
      messageId: `step_${Date.now()}`,
      payload: { nodeId },
    });
  }

  /**
   * Clean up event listeners and release all in-memory state.
   *
   * The per-call Maps (rateLimits, burstBuckets) are bounded by the number
   * of unique message types (~20) so they don't grow unbounded on their
   * own, but preview Maps track plugin-controlled keys. Clearing them here
   * prevents re-mount loops in dev from accumulating state across hosts.
   */
  destroy(): void {
    for (const timer of this.initRetryTimers) clearTimeout(timer);
    this.initRetryTimers = [];
    window.removeEventListener('message', this.handleMessage);
    this.rateLimits.clear();
    this.burstBuckets.clear();
    this.previewStorage.clear();
    this.previewDataStore.clear();
    this.previewCollections.clear();
  }

  /**
   * Returns the total byte size of the preview storage Map. Cheap since
   * preview storage values are always strings.
   */
  private getPreviewStorageTotalBytes(): number {
    let total = 0;
    for (const [key, value] of this.previewStorage) {
      total += key.length + value.length;
    }
    return total;
  }

  private async handleMessage(event: MessageEvent): Promise<void> {
    // Only handle messages from our plugin iframe
    if (event.source !== this.options.iframe.contentWindow) return;

    const data = event.data;
    if (!data || typeof data !== 'object' || !data.type) return;

    // Plugin SDK requesting init (handles srcDoc module loading race condition)
    if (data.type === 'request_init') {
      this.sendInit();
      return;
    }

    // All other messages require messageId
    if (!(data as BridgeMessage).messageId) return;

    // Fire-and-forget messages (no response needed)
    if (data.type === 'ready') {
      // Plugin acknowledged init — stop retrying
      for (const timer of this.initRetryTimers) clearTimeout(timer);
      this.initRetryTimers = [];
      return;
    }

    if (data.type === 'resize') {
      const { height } = data.payload as { height: number };
      if (typeof height === 'number' && height > 0) {
        this.options.onResize?.(height);
      }
      return;
    }

    if (data.type === 'toast') {
      const { message, variant } = data.payload as { message: string; variant: string };
      if (typeof message === 'string') {
        this.options.onToast?.(message, variant ?? 'info');
      }
      return;
    }

    if (data.type === 'navigate') {
      const { path } = data.payload as { path: string };
      if (typeof path !== 'string' || path.length === 0) {
        const message = `Bridge navigate rejected: path must be a non-empty string (got: ${typeof path === 'string' ? '<empty>' : '<missing>'})`;
        this.options.onLog?.('error', message);
        this.sendError(data.messageId, message);
        return;
      }
      // Plugins navigate in their own URL space (e.g. `/edit/<id>` or
      // `/shared/<id>`); the platform's URL space is `/a/<archeId>/...`.
      // Auto-prepend the plugin's own arche prefix when the plugin sends
      // a root-relative path so plugin code doesn't have to know its own
      // archeId or the platform path convention. Mirrors how `currentPath`
      // is delivered inbound — both sides agree on the platform path.
      //
      // Security: we always scope to *this plugin's own* archeId. A path
      // already in `/a/<own-archeId>/...` form is passed through; one
      // pointing at a different arche is rejected so a plugin can't
      // navigate the parent window into someone else's arche space.
      const ownPrefix = `/a/${this.options.archId}`;
      let resolved: string | null = null;
      if (path === ownPrefix || path.startsWith(`${ownPrefix}/`)) {
        resolved = path;
      } else if (path.startsWith('/a/')) {
        const message = `Bridge navigate rejected: cross-arche navigation not allowed (got: ${path})`;
        this.options.onLog?.('error', message);
        this.sendError(data.messageId, message);
        return;
      } else if (path.startsWith('/')) {
        resolved = path === '/' ? ownPrefix : `${ownPrefix}${path}`;
      }
      if (!resolved) {
        const message = `Bridge navigate rejected: path must be root-relative (got: ${path})`;
        this.options.onLog?.('error', message);
        this.sendError(data.messageId, message);
        return;
      }
      this.options.onNavigate?.(resolved);
      // Acknowledge so the plugin's `await bridge.request('navigate', ...)`
      // resolves instead of timing out 30s later.
      this.sendResponse(data.messageId, null);
      return;
    }

    // Request/response messages — check permissions and rate limits
    try {
      const result = await this.handleRequest(data);
      this.sendResponse(data.messageId, result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      // Surface the error to the preview log stream so get_preview_logs picks
      // it up. Without this the plugin gets a rejected Promise but the AI
      // has no visibility unless the plugin code explicitly console.errors.
      // BridgeBurstError is the exception: the burst path manages its own
      // onLog (fires once on breach, silent during cooldown) — re-logging
      // here would double-log the breach and drown the logs during cooldown.
      if (!(err instanceof BridgeBurstError)) {
        this.options.onLog?.('error', `Bridge request failed (${data.type}): ${message}`);
      }
      this.sendError(data.messageId, message);
    }
  }

  private async handleRequest(msg: BridgeMessage): Promise<unknown> {
    // In preview mode, skip permission checks (all permissions granted)
    if (!this.options.previewMode) {
      const requiredPermission = PERMISSION_MAP[msg.type];
      if (requiredPermission && !this.options.permissions.includes(requiredPermission)) {
        throw new Error(`Permission denied: ${requiredPermission} not granted`);
      }
    }

    // Rate limiting applies in all modes (protects against runaway loops in preview)
    this.checkRateLimit(msg.type, msg.payload);

    switch (msg.type) {
      case 'get_user':
        return this.options.getUser();

      case 'get_theme':
        return this.options.getTheme();

      case 'entity_invoke': {
        const payload = msg.payload as {
          entityId: string;
          input: string;
          stream?: boolean;
          systemPrompt?: string;
        };
        if (this.options.previewMode && this.options.onEntityInvoke) {
          if (payload.stream) {
            return this.handlePreviewStreamingInvoke(msg.messageId, payload);
          }
          return this.options.onEntityInvoke(payload);
        }
        // Published plugins: handle streaming via SSE through the bridge endpoint
        if (payload.stream) {
          return this.handlePublishedStreamingInvoke(msg.messageId, payload);
        }
        return this.proxyToBackend(msg);
      }

      case 'image_generate': {
        const imgPayload = msg.payload as {
          entityId: string;
          prompt: string;
          size?: string;
          quality?: string;
          style?: string;
        };
        if (this.options.previewMode && this.options.onImageGenerate) {
          return this.options.onImageGenerate(imgPayload);
        }
        return this.proxyToBackend(msg);
      }

      case 'storage_read': {
        if (this.options.previewMode) {
          if (this.options.onStorage) {
            const { path } = msg.payload as { path?: string };
            return this.options.onStorage('read', { path });
          }
          const { path } = msg.payload as { path?: string };
          const content = this.previewStorage.get(path ?? '') ?? null;
          return { content, exists: content !== null };
        }
        return this.proxyToBackend(msg);
      }

      case 'storage_write': {
        if (this.options.previewMode) {
          if (this.options.onStorage) {
            const { path, content } = msg.payload as { path?: string; content?: string };
            return this.options.onStorage('write', { path, content });
          }
          const { path, content } = msg.payload as { path?: string; content?: string };
          if (path && content !== undefined) {
            // Enforce preview storage caps so a preview plugin can't pin
            // the renderer tab with unbounded writes.
            const isNewEntry = !this.previewStorage.has(path);
            if (isNewEntry && this.previewStorage.size >= PREVIEW_STORAGE_MAX_ENTRIES) {
              throw new Error(
                `Preview storage entry limit exceeded (${PREVIEW_STORAGE_MAX_ENTRIES} entries).`,
              );
            }
            const priorBytes = isNewEntry
              ? 0
              : path.length + (this.previewStorage.get(path)?.length ?? 0);
            const newBytes = path.length + content.length;
            if (
              this.getPreviewStorageTotalBytes() - priorBytes + newBytes >
              PREVIEW_STORAGE_MAX_TOTAL_BYTES
            ) {
              throw new Error(
                `Preview storage size limit exceeded (${PREVIEW_STORAGE_MAX_TOTAL_BYTES} bytes).`,
              );
            }
            this.previewStorage.set(path, content);
          }
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      case 'storage_list': {
        if (this.options.previewMode) {
          if (this.options.onStorage) {
            const { prefix } = msg.payload as { prefix?: string };
            return this.options.onStorage('list', { prefix });
          }
          const { prefix } = msg.payload as { prefix?: string };
          const files = [...this.previewStorage.keys()].filter(
            (k) => !prefix || k.startsWith(prefix),
          );
          return { files };
        }
        return this.proxyToBackend(msg);
      }

      case 'storage_delete': {
        if (this.options.previewMode) {
          if (this.options.onStorage) {
            const { path } = msg.payload as { path?: string };
            return this.options.onStorage('delete', { path });
          }
          const { path } = msg.payload as { path?: string };
          if (path) this.previewStorage.delete(path);
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      // ── Data Store (in-memory for preview mode) ─────────────────────

      case 'data_create_collection': {
        if (this.options.previewMode) {
          const { name, userScope } = msg.payload as { name: string; userScope?: string };
          if (!this.previewDataStore.has(name)) {
            this.previewDataStore.set(name, new Map());
          }
          const meta = {
            name,
            userScope: userScope ?? 'user',
            createdAt: new Date().toISOString(),
          };
          this.previewCollections.set(name, meta);
          return { collection: meta };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_list_collections': {
        if (this.options.previewMode) {
          return { collections: [...this.previewCollections.values()] };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_delete_collection': {
        if (this.options.previewMode) {
          const { name } = msg.payload as { name: string };
          this.previewDataStore.delete(name);
          this.previewCollections.delete(name);
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_put': {
        if (this.options.previewMode) {
          const { collection, key, data } = msg.payload as {
            collection: string;
            key: string;
            data: Record<string, unknown>;
          };
          // Enforce preview data store caps — per-doc size + per-collection
          // entry count. Blocks unbounded writes in preview mode.
          const serialized = JSON.stringify(data);
          if (serialized.length > PREVIEW_DATASTORE_MAX_DOC_BYTES) {
            throw new Error(
              `Preview data store document size exceeds limit (${PREVIEW_DATASTORE_MAX_DOC_BYTES} bytes).`,
            );
          }
          let col = this.previewDataStore.get(collection);
          if (!col) {
            // Auto-create collection on first put
            col = new Map();
            this.previewDataStore.set(collection, col);
            this.previewCollections.set(collection, {
              name: collection,
              userScope: 'user',
              createdAt: new Date().toISOString(),
            });
          }
          if (!col.has(key) && col.size >= PREVIEW_DATASTORE_MAX_DOCS_PER_COLLECTION) {
            throw new Error(
              `Preview data store collection "${collection}" exceeds document limit (${PREVIEW_DATASTORE_MAX_DOCS_PER_COLLECTION}).`,
            );
          }
          col.set(key, { ...data, _key: key, _updatedAt: new Date().toISOString() });
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_get': {
        if (this.options.previewMode) {
          const { collection, key } = msg.payload as { collection: string; key: string };
          const doc = this.previewDataStore.get(collection)?.get(key) ?? null;
          return { data: doc };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_query': {
        if (this.options.previewMode) {
          const { collection, limit, orderBy } = msg.payload as {
            collection: string;
            filters?: Array<{ field: string; op: string; value: unknown }>;
            orderBy?: { field: string; direction: 'asc' | 'desc' };
            limit?: number;
          };
          const col = this.previewDataStore.get(collection);
          if (!col) return { documents: [], nextCursor: null };
          let docs = [...col.entries()].map(([k, v]) => ({
            key: k,
            data: v,
            createdAt: (v._updatedAt as string) ?? new Date().toISOString(),
            updatedAt: (v._updatedAt as string) ?? new Date().toISOString(),
          }));
          if (orderBy) {
            docs.sort((a, b) => {
              const aVal = a.data[orderBy.field];
              const bVal = b.data[orderBy.field];
              if (aVal === bVal) return 0;
              const cmp = aVal != null && bVal != null && aVal < bVal ? -1 : 1;
              return orderBy.direction === 'desc' ? -cmp : cmp;
            });
          }
          if (limit) docs = docs.slice(0, limit);
          return { documents: docs, nextCursor: null };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_delete': {
        if (this.options.previewMode) {
          const { collection, key } = msg.payload as { collection: string; key: string };
          this.previewDataStore.get(collection)?.delete(key);
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      // ── Store / IAP ────────────────────────────────────────────────

      case 'store_get_products': {
        if (this.options.previewMode) {
          return this.options.previewStoreProducts ?? [];
        }
        return this.proxyToBackend(msg);
      }

      case 'store_purchase': {
        if (this.options.previewMode) {
          // In preview, simulate a successful purchase
          const { productIdentifier } = msg.payload as { productIdentifier: string };
          return {
            success: true,
            purchaseId: `preview_apc_${Date.now()}`,
            product: { productIdentifier, productType: 'consumable', name: productIdentifier },
          };
        }
        // Published mode: delegate to purchase confirmation callback if available
        if (this.options.onPurchaseRequest) {
          const { productIdentifier } = msg.payload as { productIdentifier: string };
          // Look up product info from backend first
          const products = (await this.proxyToBackend({
            ...msg,
            type: 'store_get_products' as BridgeMessage['type'],
            payload: {},
          })) as Array<{
            productIdentifier: string;
            name: string;
            description: string | null;
            priceCredits: number;
            type: string;
          }>;
          const product = Array.isArray(products)
            ? products.find((p) => p.productIdentifier === productIdentifier)
            : undefined;
          if (!product) {
            return { success: false, error: 'Product not found' };
          }
          // Delegate to confirmation UI
          return this.options.onPurchaseRequest(product);
        }
        // Fallback: proxy directly (no confirmation overlay)
        return this.proxyToBackend(msg);
      }

      case 'store_get_entitlements':
      case 'store_get_purchase_history':
      case 'store_restore':
      case 'store_cancel_subscription': {
        if (this.options.previewMode) {
          // In preview, return empty results
          if (msg.type === 'store_get_entitlements' || msg.type === 'store_restore') return [];
          if (msg.type === 'store_get_purchase_history') return { purchases: [] };
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      // ── User Files (Fias filesystem, scoped to source_arche_id) ────
      // The server-side bridge handler enforces source_arche_id == this arche
      // and proxies to the existing arche-files routes. Preview mode has no
      // backing user_files store, so calls return empty results / a synthetic
      // URL just sufficient to keep render paths from crashing.
      case 'files_list': {
        if (this.options.previewMode) {
          return { files: [], nextCursor: null };
        }
        return this.proxyToBackend(msg);
      }
      case 'files_read': {
        if (this.options.previewMode) {
          return { content: '', contentType: 'text/plain' };
        }
        return this.proxyToBackend(msg);
      }
      case 'files_download_url': {
        if (this.options.previewMode) {
          // Synthetic non-functional URL — preview mode has no real S3 file.
          return { url: 'about:blank', expiresAt: Date.now() + 60_000 };
        }
        return this.proxyToBackend(msg);
      }
      case 'files_write': {
        if (this.options.previewMode) {
          // Echo a synthetic fileId so plugin code paths can continue.
          return { fileId: `preview_file_${Date.now()}` };
        }
        return this.proxyToBackend(msg);
      }
      case 'files_update':
      case 'files_delete': {
        if (this.options.previewMode) {
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      // ── Arche Asset Library (contributor-curated published manifest) ──
      // The bridge backend resolves the manifest from the URL path's
      // archeId (server-trusted) and signs short-lived URLs against
      // the published entries. Preview mode has no manifest, so we
      // return an empty list / a synthetic URL.
      case 'arche_assets_list': {
        if (this.options.previewMode) {
          return { entries: [], nextCursor: null };
        }
        return this.proxyToBackend(msg);
      }
      case 'arche_assets_get_url': {
        if (this.options.previewMode) {
          return {
            assetId: 'preview',
            signedUrl: 'about:blank',
            expiresAt: Math.floor(Date.now() / 1000) + 60,
            contentType: 'image/png',
            width: 0,
            height: 0,
            sizeBytes: 0,
            name: null,
            altText: null,
            tags: [],
          };
        }
        return this.proxyToBackend(msg);
      }

      default:
        throw new Error(`Unknown bridge call type: ${msg.type}`);
    }
  }

  /**
   * Handle streaming entity invoke in preview mode via SSE.
   * Sends stream_token messages to the plugin as tokens arrive,
   * then returns the final result for the normal response message.
   */
  private async handlePreviewStreamingInvoke(
    messageId: string,
    payload: { entityId: string; input: string },
  ): Promise<unknown> {
    const token = await this.options.getAuthToken();
    const response = await fetch(
      `${this.options.bridgeApiUrl}/arches/plugin-builder/sessions/${this.options.archId.replace('preview_', '')}/ai-invoke`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ input: payload.input, entityId: payload.entityId, stream: true }),
      },
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'AI invocation failed' }));
      throw new Error(err.error?.message ?? 'AI invocation failed');
    }

    // If SSE streaming, consume the stream
    if (response.headers.get('content-type')?.includes('text/event-stream') && response.body) {
      let fullOutput = '';
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.text) {
              fullOutput += parsed.text;
              // Forward token to plugin iframe
              this.options.iframe.contentWindow?.postMessage(
                { type: 'stream_token', messageId, text: parsed.text },
                '*',
              );
            }
            if (parsed.done && parsed.output) {
              fullOutput = parsed.output;
            }
          } catch {
            // Skip malformed SSE data
          }
        }
      }

      return { output: fullOutput };
    }

    // Non-streaming fallback
    return response.json();
  }

  /**
   * Handle streaming entity invoke for published plugins via the bridge endpoint.
   */
  private async handlePublishedStreamingInvoke(
    messageId: string,
    payload: { entityId: string; input: string },
  ): Promise<unknown> {
    const token = await this.options.getAuthToken();
    const response = await fetch(
      `${this.options.bridgeApiUrl}/plugins/bridge/${this.options.archId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: 'entity_invoke',
          payload: { ...payload, stream: true },
        }),
      },
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'AI invocation failed' }));
      throw new Error(err.error?.message ?? 'AI invocation failed');
    }

    if (response.headers.get('content-type')?.includes('text/event-stream') && response.body) {
      let fullOutput = '';
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.error) {
              throw new Error(parsed.error);
            }
            if (parsed.text) {
              fullOutput += parsed.text;
              this.options.iframe.contentWindow?.postMessage(
                { type: 'stream_token', messageId, text: parsed.text },
                '*',
              );
            }
            if (parsed.done && parsed.output) {
              fullOutput = parsed.output;
            }
          } catch (e) {
            if (e instanceof Error && e.message !== 'AI invocation failed') throw e;
          }
        }
      }

      return { output: fullOutput };
    }

    // Non-SSE fallback
    return response.json();
  }

  private async proxyToBackend(msg: BridgeMessage): Promise<unknown> {
    const token = await this.options.getAuthToken();
    const response = await fetch(
      `${this.options.bridgeApiUrl}/plugins/bridge/${this.options.archId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: msg.type,
          payload: msg.payload,
        }),
      },
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Bridge call failed' }));
      throw new Error(err.error?.message ?? 'Bridge call failed');
    }

    return response.json();
  }

  private checkRateLimit(type: string, payload: unknown): void {
    // Burst detection first (catches runaway loops in <5s)
    this.checkBurst(type, payload);

    const limit = RATE_LIMITS[type];
    if (!limit) return;

    const now = Date.now();
    const bucket = this.rateLimits.get(type);

    if (!bucket || now - bucket.windowStart > 60_000) {
      this.rateLimits.set(type, { count: 1, windowStart: now });
      return;
    }

    if (bucket.count >= limit.maxPerMinute) {
      const target = describeBridgeTarget(type, payload);
      const targetSuffix = target ? ` (${target})` : '';
      throw new Error(
        `Rate limit exceeded for ${type}${targetSuffix}: max ${limit.maxPerMinute}/minute`,
      );
    }

    bucket.count++;
  }

  private checkBurst(type: string, payload: unknown): void {
    const now = Date.now();
    const bucket = this.burstBuckets.get(type);
    const target = describeBridgeTarget(type, payload);
    const targetSuffix = target ? ` (${target})` : '';

    // If currently in cooldown, block silently (onLog already fired on first breach)
    if (bucket?.blockedUntil && now < bucket.blockedUntil) {
      throw new BridgeBurstError(
        `Bridge call "${type}"${targetSuffix} temporarily blocked — rapid-fire loop detected. ` +
          `Check useEffect dependency arrays, or debounce writes if this is a usePersistentState key being updated at high frequency.`,
      );
    }

    // Reset expired burst windows
    if (!bucket || now - bucket.windowStart > BURST_WINDOW_MS) {
      this.burstBuckets.set(type, { count: 1, windowStart: now });
      return;
    }

    bucket.count++;

    if (bucket.count > BURST_THRESHOLD) {
      bucket.blockedUntil = now + BURST_COOLDOWN_MS;
      const msg =
        `Runaway loop detected: "${type}"${targetSuffix} called ${bucket.count} times in ${BURST_WINDOW_MS / 1000}s. ` +
        `Calls blocked for ${BURST_COOLDOWN_MS / 1000}s. ` +
        `Check useEffect dependency arrays, or debounce writes if this is a usePersistentState key being updated at high frequency.`;
      this.options.onLog?.('error', msg);
      throw new BridgeBurstError(
        `Bridge call "${type}"${targetSuffix} temporarily blocked — rapid-fire loop detected.`,
      );
    }
  }

  private sendToPlugin(message: BridgeMessage): void {
    // targetOrigin '*' is required because sandboxed iframes without allow-same-origin
    // have an opaque (null) origin. Security is enforced by checking event.source
    // against iframe.contentWindow on incoming messages (line 113).
    this.options.iframe.contentWindow?.postMessage(message, '*');
  }

  private sendResponse(messageId: string, payload: unknown): void {
    this.sendToPlugin({
      type: 'response' as string,
      messageId,
      payload,
    });
  }

  private sendError(messageId: string, error: string): void {
    // See sendToPlugin() for why '*' is used as targetOrigin.
    this.options.iframe.contentWindow?.postMessage(
      {
        type: 'response',
        messageId,
        payload: null,
        error,
      },
      '*',
    );
  }
}
